/**
 * Config-driven route / path translation map.
 *
 * Every page in the app is identified by a stable, locale-independent
 * `RouteKey`. For each key we declare the localized URL segment(s) per locale.
 * This is the single place that knows how a logical page maps to a URL in each
 * language, so:
 *
 *   - the file-based router (EN at root, DE under `/de`) stays a thin wrapper,
 *   - the language toggle can map the current URL to its equivalent in the
 *     other locale (`/de/kurse` <-> `/courses`),
 *   - hreflang alternates, canonicals and per-locale sitemaps are all derived
 *     from one source,
 *   - old German root URLs (`/kurse`, ...) can be 301-redirected to `/de/...`.
 *
 * Adding a route or a locale is a single edit here - no string is duplicated
 * across the routing, SEO, nav or redirect layers.
 */

import { DEFAULT_LOCALE, LOCALES, localePrefix } from './config'
import type { Locale } from './config'

/**
 * Localized segments for a single route, keyed by locale.
 *
 * `segments` is the path relative to the locale root, e.g. `['courses']` ->
 * `/courses` (en) and `/kurse` (de) -> `/de/kurse`. Dynamic params are written
 * as `$param` tokens, matching TanStack's file-route param syntax, e.g.
 * `['courses', '$courseSlug', '$lessonSlug']`.
 */
export type RouteDefinition = {
  /**
   * Localized path segments per locale (no leading slash, no locale prefix).
   * Every shipped locale is required, so adding a locale to {@link LOCALES}
   * without giving it slugs here is a compile error rather than a silent
   * English-slug URL leaking into sitemaps and hreflang.
   */
  segments: Record<Locale, string[]>
  /**
   * Whether this route is publicly indexable. Private/auth/account/admin
   * routes are `false` so they are excluded from sitemaps and hreflang.
   */
  indexable: boolean
}

/** Resolve the localized segments for a route in a given locale. */
function routeSegments(
  definition: RouteDefinition,
  locale: Locale,
): string[] {
  return definition.segments[locale]
}

export const ROUTES = {
  home: {
    segments: { en: [], de: [], fr: [], it: [] },
    indexable: true,
  },
  courses: {
    segments: { en: ['courses'], de: ['kurse'], fr: ['cours'], it: ['corsi'] },
    indexable: true,
  },
  course: {
    segments: {
      en: ['courses', '$courseSlug'],
      de: ['kurse', '$courseSlug'],
      fr: ['cours', '$courseSlug'],
      it: ['corsi', '$courseSlug'],
    },
    indexable: true,
  },
  lesson: {
    segments: {
      en: ['courses', '$courseSlug', '$lessonSlug'],
      de: ['kurse', '$courseSlug', '$lessonSlug'],
      fr: ['cours', '$courseSlug', '$lessonSlug'],
      it: ['corsi', '$courseSlug', '$lessonSlug'],
    },
    indexable: true,
  },
  knowledge: {
    segments: {
      en: ['knowledge'],
      de: ['wissen'],
      fr: ['savoir'],
      it: ['sapere'],
    },
    indexable: true,
  },
  article: {
    segments: {
      en: ['knowledge', '$articleSlug'],
      de: ['wissen', '$articleSlug'],
      fr: ['savoir', '$articleSlug'],
      it: ['sapere', '$articleSlug'],
    },
    indexable: true,
  },
  fundamentals: {
    segments: {
      en: ['fundamentals'],
      de: ['grundlagen'],
      fr: ['fondamentaux'],
      it: ['fondamenti'],
    },
    indexable: true,
  },
  fundamental: {
    segments: {
      en: ['fundamentals', '$fundamentalSlug'],
      de: ['grundlagen', '$fundamentalSlug'],
      fr: ['fondamentaux', '$fundamentalSlug'],
      it: ['fondamenti', '$fundamentalSlug'],
    },
    indexable: true,
  },
  builds: {
    segments: {
      en: ['builds'],
      de: ['projekte'],
      fr: ['projets'],
      it: ['progetti'],
    },
    indexable: true,
  },
  build: {
    segments: {
      en: ['builds', '$buildSlug'],
      de: ['projekte', '$buildSlug'],
      fr: ['projets', '$buildSlug'],
      it: ['progetti', '$buildSlug'],
    },
    indexable: true,
  },
  glossary: {
    segments: {
      en: ['glossary'],
      de: ['glossar'],
      fr: ['glossaire'],
      it: ['glossario'],
    },
    indexable: true,
  },
  glossaryTerm: {
    segments: {
      en: ['glossary', '$termSlug'],
      de: ['glossar', '$termSlug'],
      fr: ['glossaire', '$termSlug'],
      it: ['glossario', '$termSlug'],
    },
    indexable: true,
  },
  compare: {
    segments: {
      en: ['compare'],
      de: ['vergleich'],
      fr: ['comparer'],
      it: ['confronta'],
    },
    indexable: true,
  },
  comparison: {
    segments: {
      en: ['compare', '$comparisonSlug'],
      de: ['vergleich', '$comparisonSlug'],
      fr: ['comparer', '$comparisonSlug'],
      it: ['confronta', '$comparisonSlug'],
    },
    indexable: true,
  },
  guides: {
    segments: {
      en: ['guides'],
      de: ['ratgeber'],
      fr: ['guides'],
      it: ['guide'],
    },
    indexable: true,
  },
  guide: {
    segments: {
      en: ['guides', '$guideSlug'],
      de: ['ratgeber', '$guideSlug'],
      fr: ['guides', '$guideSlug'],
      it: ['guide', '$guideSlug'],
    },
    indexable: true,
  },
  tools: {
    segments: {
      en: ['tools'],
      de: ['tools'],
      fr: ['outils'],
      it: ['strumenti'],
    },
    indexable: true,
  },
  tool: {
    segments: {
      en: ['tools', '$toolSlug'],
      de: ['tools', '$toolSlug'],
      fr: ['outils', '$toolSlug'],
      it: ['strumenti', '$toolSlug'],
    },
    indexable: true,
  },
  about: {
    segments: {
      en: ['about'],
      de: ['ueber'],
      fr: ['a-propos'],
      it: ['chi-siamo'],
    },
    indexable: true,
  },
  pricing: {
    segments: {
      en: ['pricing'],
      de: ['preise'],
      fr: ['tarifs'],
      it: ['prezzi'],
    },
    indexable: true,
  },
  resources: {
    segments: {
      en: ['resources'],
      de: ['ressourcen'],
      fr: ['ressources'],
      it: ['risorse'],
    },
    indexable: true,
  },
  resource: {
    segments: {
      en: ['resources', '$resourceSlug'],
      de: ['ressourcen', '$resourceSlug'],
      fr: ['ressources', '$resourceSlug'],
      it: ['risorse', '$resourceSlug'],
    },
    indexable: true,
  },
  community: {
    segments: {
      en: ['community'],
      de: ['community'],
      fr: ['communaute'],
      it: ['community'],
    },
    indexable: true,
  },
  communityTopics: {
    segments: {
      en: ['community', 'topics'],
      de: ['community', 'themen'],
      fr: ['communaute', 'themes'],
      it: ['community', 'temi'],
    },
    indexable: true,
  },
  communityTopic: {
    segments: {
      en: ['community', 'topics', '$topicSlug'],
      de: ['community', 'themen', '$topicSlug'],
      fr: ['communaute', 'themes', '$topicSlug'],
      it: ['community', 'temi', '$topicSlug'],
    },
    indexable: true,
  },
  communityPost: {
    segments: {
      en: ['community', 'posts', '$postSlug'],
      de: ['community', 'beitraege', '$postSlug'],
      fr: ['communaute', 'publications', '$postSlug'],
      it: ['community', 'post', '$postSlug'],
    },
    indexable: true,
  },
  search: {
    segments: {
      en: ['search'],
      de: ['suche'],
      fr: ['recherche'],
      it: ['ricerca'],
    },
    indexable: true,
  },
  certificate: {
    segments: {
      en: ['certificates', '$certificateId'],
      de: ['zertifikate', '$certificateId'],
      fr: ['certificats', '$certificateId'],
      it: ['certificati', '$certificateId'],
    },
    indexable: true,
  },
  changelog: {
    segments: {
      en: ['changelog'],
      de: ['changelog'],
      fr: ['changelog'],
      it: ['changelog'],
    },
    indexable: true,
  },
  changelogEntry: {
    segments: {
      en: ['changelog', '$entrySlug'],
      de: ['changelog', '$entrySlug'],
      fr: ['changelog', '$entrySlug'],
      it: ['changelog', '$entrySlug'],
    },
    indexable: true,
  },
  newsletter: {
    segments: {
      en: ['newsletter'],
      de: ['newsletter'],
      fr: ['newsletter'],
      it: ['newsletter'],
    },
    indexable: true,
  },
  // Double opt-in landing (newsletter + founding list), reached from the
  // confirmation email with `?token=` (M3). Private: never in sitemaps.
  emailConfirm: {
    segments: {
      en: ['confirm'],
      de: ['bestaetigen'],
      fr: ['confirmer'],
      it: ['conferma'],
    },
    indexable: false,
  },
  // Unsubscribe + preference center, reached from a signed email link (M3).
  emailUnsubscribe: {
    segments: {
      en: ['unsubscribe'],
      de: ['abmelden'],
      fr: ['desabonnement'],
      it: ['disiscrizione'],
    },
    indexable: false,
  },
  contact: {
    segments: {
      en: ['contact'],
      de: ['kontakt'],
      fr: ['contact'],
      it: ['contatti'],
    },
    indexable: true,
  },
  progress: {
    segments: {
      en: ['progress'],
      de: ['fortschritt'],
      fr: ['progression'],
      it: ['progressi'],
    },
    indexable: false,
  },
  saved: {
    segments: {
      en: ['saved'],
      de: ['gespeichert'],
      fr: ['enregistres'],
      it: ['salvati'],
    },
    indexable: false,
  },
  settings: {
    segments: {
      en: ['settings'],
      de: ['einstellungen'],
      fr: ['parametres'],
      it: ['impostazioni'],
    },
    indexable: false,
  },
  settingsProfile: {
    segments: {
      en: ['settings', 'profile'],
      de: ['einstellungen', 'profil'],
      fr: ['parametres', 'profil'],
      it: ['impostazioni', 'profilo'],
    },
    indexable: false,
  },
  settingsNotifications: {
    segments: {
      en: ['settings', 'notifications'],
      de: ['einstellungen', 'benachrichtigungen'],
      fr: ['parametres', 'notifications'],
      it: ['impostazioni', 'notifiche'],
    },
    indexable: false,
  },
  settingsPrivacy: {
    segments: {
      en: ['settings', 'privacy'],
      de: ['einstellungen', 'datenschutz'],
      fr: ['parametres', 'confidentialite'],
      it: ['impostazioni', 'privacy'],
    },
    indexable: false,
  },
  // Plan, renewal, Stripe customer portal and Team seats (M2). Also the
  // Stripe Checkout success return target (`?checkout=success`).
  settingsBilling: {
    segments: {
      en: ['settings', 'billing'],
      de: ['einstellungen', 'abrechnung'],
      fr: ['parametres', 'facturation'],
      it: ['impostazioni', 'abbonamento'],
    },
    indexable: false,
  },
  login: {
    segments: { en: ['login'], de: ['login'], fr: ['login'], it: ['login'] },
    indexable: false,
  },
  register: {
    segments: {
      en: ['register'],
      de: ['registrieren'],
      fr: ['inscription'],
      it: ['registrazione'],
    },
    indexable: false,
  },
  onboarding: {
    segments: {
      en: ['onboarding'],
      de: ['onboarding'],
      fr: ['onboarding'],
      it: ['onboarding'],
    },
    indexable: false,
  },
  admin: {
    segments: { en: ['admin'], de: ['admin'], fr: ['admin'], it: ['admin'] },
    indexable: false,
  },
  adminSection: {
    segments: {
      en: ['admin', '$section'],
      de: ['admin', '$section'],
      fr: ['admin', '$section'],
      it: ['admin', '$section'],
    },
    indexable: false,
  },
  terms: {
    segments: {
      en: ['terms'],
      de: ['agb'],
      fr: ['conditions'],
      it: ['termini'],
    },
    indexable: true,
  },
  imprint: {
    segments: {
      en: ['imprint'],
      de: ['impressum'],
      fr: ['mentions-legales'],
      it: ['note-legali'],
    },
    indexable: true,
  },
  privacy: {
    segments: {
      en: ['privacy'],
      de: ['datenschutz'],
      fr: ['confidentialite'],
      it: ['privacy'],
    },
    indexable: true,
  },
  accessibility: {
    segments: {
      en: ['accessibility'],
      de: ['barrierefreiheit'],
      fr: ['accessibilite'],
      it: ['accessibilita'],
    },
    indexable: true,
  },
  cookies: {
    segments: {
      en: ['cookies'],
      de: ['cookies'],
      fr: ['cookies'],
      it: ['cookies'],
    },
    indexable: true,
  },
  apiDocs: {
    segments: {
      en: ['api-docs'],
      de: ['api-docs'],
      fr: ['api-docs'],
      it: ['api-docs'],
    },
    indexable: true,
  },
} as const satisfies Record<string, RouteDefinition>

export type RouteKey = keyof typeof ROUTES

export type RouteParams = Record<string, string>

function fillSegments(segments: readonly string[], params: RouteParams): string[] {
  return segments.map((segment) => {
    if (segment.startsWith('$')) {
      const name = segment.slice(1)
      if (!Object.prototype.hasOwnProperty.call(params, name)) {
        throw new Error(`Missing route param "${name}" for segment "${segment}"`)
      }
      return params[name]
    }
    return segment
  })
}

/**
 * Build an absolute, locale-prefixed path for a logical route.
 *
 * @example
 *   localizedPath('courses', 'en')                       // '/courses'
 *   localizedPath('courses', 'de')                       // '/de/kurse'
 *   localizedPath('home', 'de')                          // '/de'
 *   localizedPath('lesson', 'de', { courseSlug: 'a', lessonSlug: 'b' })
 *     // '/de/kurse/a/b'
 */
export function localizedPath(
  key: RouteKey,
  locale: Locale,
  params: RouteParams = {},
): string {
  const filled = fillSegments(routeSegments(ROUTES[key], locale), params)
  const prefix = localePrefix(locale)
  const tail = filled.join('/')
  if (!tail) return prefix || '/'
  return `${prefix}/${tail}`
}

/**
 * The pattern TanStack `createFileRoute` expects for a given route + locale,
 * e.g. `/de/kurse/$courseSlug/$lessonSlug`. Used by the thin route wrappers so
 * the registered pattern always agrees with this map.
 */
export function routePattern(key: RouteKey, locale: Locale): string {
  const segments = routeSegments(ROUTES[key], locale)
  const prefix = localePrefix(locale)
  const tail = segments.join('/')
  if (!tail) return prefix || '/'
  return `${prefix}/${tail}`
}

type ParsedRoute = { key: RouteKey; locale: Locale; params: RouteParams }

/**
 * Reverse lookup: resolve a concrete pathname back to its `RouteKey`, locale
 * and extracted params. Returns `null` for paths that are not in the map.
 *
 * This is what powers the language toggle ("what is the equivalent page in the
 * other locale?") and the old-URL redirect table.
 */
export function matchRoute(pathname: string): ParsedRoute | null {
  const clean = pathname.split('?')[0].split('#')[0]
  const segments = clean.split('/').filter(Boolean)

  let locale: Locale = DEFAULT_LOCALE
  let pathSegments = segments
  const maybeLocale = segments[0]
  if (
    maybeLocale &&
    maybeLocale !== DEFAULT_LOCALE &&
    (LOCALES as readonly string[]).includes(maybeLocale)
  ) {
    locale = maybeLocale as Locale
    pathSegments = segments.slice(1)
  }

  for (const key of Object.keys(ROUTES) as RouteKey[]) {
    const template = routeSegments(ROUTES[key], locale)
    if (template.length !== pathSegments.length) continue

    const params: RouteParams = {}
    let matched = true
    for (let i = 0; i < template.length; i += 1) {
      const tpl = template[i]
      const actual = pathSegments[i]
      if (tpl.startsWith('$')) {
        params[tpl.slice(1)] = decodeURIComponent(actual)
      } else if (tpl !== actual) {
        matched = false
        break
      }
    }
    if (matched) return { key, locale, params }
  }

  return null
}

/**
 * Map the current pathname to its equivalent in `targetLocale`, preserving the
 * page and its params. Falls back to the locale root when the path is unknown
 * (so the toggle always lands somewhere sensible).
 *
 * @example
 *   equivalentPath('/de/kurse/a/b', 'en') // '/courses/a/b'
 *   equivalentPath('/courses', 'de')      // '/de/kurse'
 */
export function equivalentPath(pathname: string, targetLocale: Locale): string {
  const match = matchRoute(pathname)
  if (!match) return localePrefix(targetLocale) || '/'
  return localizedPath(match.key, targetLocale, match.params)
}

/**
 * Compute hreflang alternates for a route. Returns one entry per locale plus an
 * `x-default` pointing at the default locale, as absolute paths (the caller
 * prepends the site origin).
 */
export function alternatesFor(
  key: RouteKey,
  params: RouteParams = {},
): Array<{ hrefLang: string; path: string }> {
  const perLocale = LOCALES.map((locale) => ({
    hrefLang: locale,
    path: localizedPath(key, locale, params),
  }))
  return [
    ...perLocale,
    { hrefLang: 'x-default', path: localizedPath(key, DEFAULT_LOCALE, params) },
  ]
}

/** Every public, indexable route key (static pages only - no dynamic params). */
export function indexableStaticKeys(): RouteKey[] {
  return (Object.keys(ROUTES) as RouteKey[]).filter((key) => {
    const def = ROUTES[key]
    if (!def.indexable) return false
    return routeSegments(def, DEFAULT_LOCALE).every(
      (segment) => !segment.startsWith('$'),
    )
  })
}

/**
 * Localized path prefixes for every private (non-indexable) route - the
 * auth / account / admin area (login, register, onboarding, progress, saved,
 * settings*, admin*). Derived once from {@link ROUTES} across every locale, so
 * adding a private route or a locale extends this automatically. Routes that
 * are nothing but dynamic params are skipped because their static parent prefix
 * (e.g. `/admin` for `/admin/$section`) already covers them.
 */
const PRIVATE_PATH_PREFIXES: string[] = (Object.keys(ROUTES) as RouteKey[])
  .filter((key) => !ROUTES[key].indexable)
  .flatMap((key) =>
    LOCALES.flatMap((locale) => {
      const segments = routeSegments(ROUTES[key], locale)
      if (segments.length === 0 || segments.some((s) => s.startsWith('$'))) {
        return []
      }
      return [`${localePrefix(locale)}/${segments.join('/')}`]
    }),
  )

/**
 * Whether a pathname belongs to the private (behind-auth) account / admin / auth
 * area rather than a public marketing or content page. Matches the exact path or
 * any nested sub-path, in every locale. Used to scope marketing-only UI - such
 * as the support chat widget - to public pages only.
 *
 * @example
 *   isPrivatePath('/courses')          // false (public)
 *   isPrivatePath('/settings/profile') // true  (account)
 *   isPrivatePath('/de/fortschritt')   // true  (account, de)
 */
export function isPrivatePath(pathname: string): boolean {
  const clean = pathname.split('?')[0].split('#')[0].replace(/\/+$/u, '') || '/'
  return PRIVATE_PATH_PREFIXES.some(
    (prefix) => clean === prefix || clean.startsWith(`${prefix}/`),
  )
}
